import { useEffect, useMemo, useState } from 'react';
import { CloudUpload, Loader2, Plus, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useTheme } from 'components/core/theme-provider';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'components/ui/dialog';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import {
  useCompletePreSignedUpload,
  useGetLibraryPreSignedUrlForUpload,
} from 'lib/api/hooks/use-storage';
import { useAddFiles, useGetFolderList } from 'features/chat/hooks/use-kb';
import { cn } from 'lib/utils';
import { MAX_FILE_SIZE } from 'constant/file';

const MAX_FILES = 25;
const SUPPORTED_EXTENSIONS = ['pdf', 'docx', 'pptx', 'txt', 'md', 'html', 'csv', 'xlsx', 'json', 'png', 'jpg', 'jpeg'];
const supportedFileTypes = SUPPORTED_EXTENSIONS.map((ext) => ext.toUpperCase()).join(', ');
const SUPPORTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/markdown',
  'text/html',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/json',
  'image/png',
  'image/jpeg',
];

type AddKbFileModalProps = {
  folderId: string;
  projectKey: string;
};

export function AddKbFileModal({ folderId, projectKey }: AddKbFileModalProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const supportedFilesHint = t('SUPPORTED_FILES_HINT', {
    types: supportedFileTypes,
    defaultValue: `Supported files: ${supportedFileTypes}`,
  });
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const presignedUploadMutation = useGetLibraryPreSignedUrlForUpload();
  const completeUploadMutation = useCompletePreSignedUpload();
  const { mutateAsync: addFilesMutate } = useAddFiles();

  const { data: folderListData } = useGetFolderList(projectKey, !!projectKey);
  const folder = useMemo(
    () => folderListData?.data?.folders?.find((f) => f._id === folderId),
    [folderId, folderListData?.data?.folders]
  );
  const canEdit = useIsOwner(folder);

  useEffect(() => {
    const urls = files.map((file) => URL.createObjectURL(file));
    setPreviews(urls);

    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [files]);

  const resetState = () => {
    setFiles([]);
    setPreviews([]);
    setIsDragActive(false);
    setIsUploading(false);
  };

  const isMaxFilesReached = files.length >= MAX_FILES;
  const isOverFileLimit = files.length > MAX_FILES;
  const isUploadDisabled = files.length === 0 || isUploading || isOverFileLimit;
  const filesOverLimit = Math.max(0, files.length - MAX_FILES);

  const validateFile = (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    const isImageByType = file.type?.startsWith('image/') ?? false;
    const isImageByExt = ['png', 'jpg', 'jpeg'].includes(extension);
    const isSupported =
      SUPPORTED_MIME_TYPES.includes(file.type) ||
      SUPPORTED_EXTENSIONS.includes(extension) ||
      isImageByType ||
      isImageByExt;
    if (!isSupported) {
      toast({
        title: t('INVALID_FILE_TYPE'),
        description: t('FILE_TYPE_NOT_SUPPORTED', { name: file.name }),
        variant: 'destructive',
      });
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: t('FILE_TOO_LARGE'),
        description: t('FILE_EXCEEDS_SIZE_LIMIT', { name: file.name }),
        variant: 'destructive',
      });
      return false;
    }

    return true;
  };

  const handleFileSelection = (fileList: FileList | null) => {
    if (!canEdit || !fileList) return;

    const validFiles = Array.from(fileList).filter(validateFile);
    setFiles((prev) => {
      if (prev.length >= MAX_FILES) {
        toast({
          title: t('TOO_MANY_FILES'),
          description: t('MAX_25_FILES_ALLOWED'),
          variant: 'destructive',
        });
        return prev;
      }

      const combined = [...prev, ...validFiles];
      if (combined.length > MAX_FILES) {
        const excess = combined.length - MAX_FILES;
        toast({
          title: t('TOO_MANY_FILES'),
          description:
            t('FILES_SELECTION_EXCEEDS_LIMIT', {
              count: combined.length,
              max: MAX_FILES,
              excess,
            }) ||
            `You selected ${combined.length} files (maximum ${MAX_FILES}). Remove ${excess} file(s) using the × button to enable upload.`,
          variant: 'destructive',
        });
      }

      return combined;
    });
  };

  const uploadFileToStorage = async (url: string, file: File) => {
    const response = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
        'x-ms-blob-type': 'BlockBlob',
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed for ${file.name}`);
    }
  };

  const processFile = async (file: File) => {
    const presigned = await presignedUploadMutation.mutateAsync({
      name: file.name,
      projectKey,
      itemId: '',
      metaData: '',
      accessModifier: 'Public',
      configurationName: 'Default',
      parentDirectoryId: '',
      tags: '',
    });

    if (!presigned.isSuccess || !presigned.uploadUrl || !presigned.fileId) {
      throw new Error(`Failed to get upload URL for ${file.name}`);
    }

    await uploadFileToStorage(presigned.uploadUrl, file);
    return presigned.fileId;
  };

  const removeFile = (index: number) => {
    if (!canEdit) return;
    setFiles((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const uploadFileHandler = async () => {
    if (!canEdit || isUploadDisabled || files.length > MAX_FILES) return;

    setIsUploading(true);
    try {
      const fileIds = (await Promise.all(files.map(processFile))).filter(Boolean);
      if (fileIds.length === 0) {
        throw new Error(t('NO_FILES_UPLOADED'));
      }
      await completeUploadMutation.mutateAsync(fileIds);

      const response = await addFilesMutate({
        folder_id: folderId,
        project_key: projectKey,
        file_ids: fileIds,
      });

      if (!response.is_success) {
        throw new Error(
          response.detail ||
          t('FAILED_TO_ATTACH_FILES_TO_KB')
        );
      }

      toast({
        variant: 'success',
        title: t('FILES_UPLOADED'),
        description: t('FILES_ADDED_TO_KNOWLEDGE_BASE', { count: fileIds.length }),
      });
      resetState();
      setOpen(false);
    } catch (error) {
      toast({
        title: t('UPLOAD_FAILED'),
        description: error instanceof Error ? error.message : t('PLEASE_TRY_AGAIN'),
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          resetState();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" disabled={!canEdit}>
          <Plus className="h-4 w-4" />
          {t('ADD_FILE')}
        </Button>
      </DialogTrigger>
      <DialogContent
        className={cn(
          'max-h-[min(92dvh,100svh)] w-[calc(100vw-1.5rem)] gap-4 overflow-hidden p-5 sm:max-w-2xl',
          files.length > 0
            ? 'grid grid-rows-[auto_auto_minmax(0,1fr)_auto]'
            : 'grid grid-rows-[auto_auto_auto]'
        )}
      >
        <DialogHeader>
          <DialogTitle>{t('ADD_FILE')}</DialogTitle>
          <DialogDescription>{t('UPLOAD_SUPPORTED_DOCUMENTS_TO_FOLDER')}</DialogDescription>
        </DialogHeader>

        {!isMaxFilesReached ? (
          <label
            className={cn(
              'flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors',
              isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
              !canEdit ? 'pointer-events-none opacity-50' : ''
            )}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              handleFileSelection(event.dataTransfer.files);
            }}
          >
            <input
              type="file"
              accept=".pdf,.docx,.pptx,.txt,.md,.html,.csv,.xlsx,.json,.png,.jpg,.jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/markdown,text/html,text/csv,application/json,image/png,image/jpeg,image/jpg"
              multiple
              className="hidden"
              onChange={(event) => handleFileSelection(event.target.files)}
            />
            <CloudUpload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm leading-5">
              <span className={cn('font-semibold', theme === 'dark' ? 'text-true-white' : 'text-primary')}>
                {t('CLICK_TO_UPLOAD')}
              </span>
              <span> {t('OR_DRAG_AND_DROP')}</span>
            </p>
            <span className="text-xs text-muted-foreground">{supportedFilesHint}</span>
            <span className="text-xs text-muted-foreground">{t('MAX_FILES_AND_SIZE_HINT')}</span>
          </label>
        ) : (
          <p className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-center text-sm text-muted-foreground">
            {isOverFileLimit
              ? t('MAX_FILES_OVER_LIMIT_REMOVE', {
                count: files.length,
                max: MAX_FILES,
                excess: filesOverLimit,
              }) ||
              `You have ${files.length} files selected (maximum ${MAX_FILES}). Remove ${filesOverLimit} file(s) to upload.`
              : t('MAX_FILES_REACHED_REMOVE_TO_CONTINUE', { max: MAX_FILES }) ||
              `Maximum ${MAX_FILES} files selected. Remove a file to add more.`}
          </p>
        )}

        {files.length > 0 ? (
          <div className="min-h-0 overflow-y-auto overscroll-contain pr-1">
            <div className="flex flex-wrap gap-4">
              {files.map((file, index) => (
                <div key={`${file.name}-${index}`} className="flex flex-col items-center">
                  <div className="relative h-32 w-32 overflow-hidden rounded border bg-muted/20">
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="absolute right-1 top-1 z-10 rounded-full bg-background text-muted-foreground shadow hover:text-foreground"
                    >
                      <XCircle className="h-5 w-5" />
                    </button>
                    {file.type === 'application/pdf' ? (
                      <object
                        data={previews[index]}
                        type="application/pdf"
                        className="block h-full w-full"
                        aria-label={t('PDF_PREVIEW', { defaultValue: 'PDF preview' })}
                      />
                    ) : file.type?.startsWith('image/') ? (
                      <img
                        src={previews[index]}
                        alt={file.name}
                        className="block h-full w-full object-contain"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center px-3 text-center text-xs text-muted-foreground">
                        {t('FILE_PREVIEW')}
                      </div>
                    )}
                  </div>
                  <p className="mt-1 w-32 truncate text-center text-xs">{file.name}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <DialogFooter className="flex flex-col gap-2 border-t border-border bg-background p-2 sm:flex-row sm:items-center sm:justify-end">
          {files.length > 0 ? (
            <p
              className={cn(
                'w-full text-center text-xs sm:mr-auto sm:text-left',
                isOverFileLimit ? 'font-medium text-destructive' : 'text-muted-foreground'
              )}
            >
              {isOverFileLimit
                ? t('FILES_OVER_LIMIT_FOOTER', {
                  count: files.length,
                  max: MAX_FILES,
                  excess: filesOverLimit,
                }) ||
                `${files.length} files selected — remove ${filesOverLimit} to upload (max ${MAX_FILES})`
                : t('SELECTED_FILES_COUNT', { count: files.length, max: MAX_FILES }) ||
                `${files.length} / ${MAX_FILES} files selected`}
            </p>
          ) : null}
          <DialogClose asChild>
            <Button variant="outline" disabled={isUploading} className="w-full sm:w-20" size="sm">
              {t('CANCEL')}
            </Button>
          </DialogClose>
          <Button
            onClick={uploadFileHandler}
            disabled={!canEdit || isUploadDisabled}
            className="w-full sm:w-24"
            size="sm"
          >
            {isUploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('UPLOADING')}
              </>
            ) : (
              t('UPLOAD')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
